<script setup lang="ts">
import { computed } from 'vue';

import { useI18n } from 'vue-i18n';
import {
  LpFilterSelect,
  LpMarkerBtn,
  LpCheckbox,
  LpHorizontalMenu,
  LpHorizontalMenuItem,
  LpCard,
} from '@thehotelsnetwork/lampo';

import useFilterState from '@/state/useFilterState';
import useSectionState from '@/state/useSectionState';

import { track } from '@/utils/analytics';
import navigate from '@/utils/navigate';

import {
  devicesConfig,
  geolocationsConfig,
  currentGeolocationOption,
  travelPartyConfig,
  hotelTypeConfig,
  hotelCategoryConfig,
  sourceConfig,
  otasConfig,
  sectionFiltersConfig,
} from '@/config/sectionFiltersConfig';
import { premiumLandingUrl } from '@/config/redirectUrls';

import { getPipeData } from '@/state/sectionStateConfig';
import useUserState from '@/state/useUserState';

import DatesSelector from '@/modules/DataSections/components/Calendar/DatesSelector.vue';

import { stayDatesConfig, minStayDatesMonth } from '@/utils/stayDatesUtils';
import handleTrackFilter from '@/utils/handleTrackFilter';
import formatValue from '@/utils/formatValue';
import { numericFormat } from '@/config/valueFormatConfig';

type GeolocationOption = {
  countryCode?: string;
  id: number;
  name: string;
  value: number;
  label: string;
  itemCount?: number;
};

const { t } = useI18n();
const {
  geolocations,
  devices,
  source,
  travelParty,
  hotelType,
  hotelCategory,
  filteredOtas,

  sdDateBegin,
  sdDateEnd,
} = useFilterState();

const {
  dataId,
  currentPermissions,
  isFreeCalendarView,
} = useSectionState();

const geolocationVisitsPipe = getPipeData('geolocation/visits');

const { isStrictlyDMOUser } = useUserState();

const geolocationMenuItems = [
  {
    label: 'All',
    value: 'all',
  },
  {
    label: 'Country',
    value: 'country',
  },
  {
    label: 'Region',
    value: 'region',
  },
  {
    label: 'City',
    value: 'city',
  },
];

const disablePremium = computed(() => !currentGeolocationOption.value.match(/all|country/)
      && !isStrictlyDMOUser.value
      && !currentPermissions.value.geofilter);

const formatNumber = (n: number) => formatValue(n, numericFormat);

const handleUpgradeClick = () => {
  track('premium - click - geolocation');

  navigate(premiumLandingUrl, { external: true, newTab: true });
};

</script>

<template>
  <div
    v-if="sectionFiltersConfig.active"
    :key="dataId"
    class="relative flex items-center gap-4 filters z-90 section-filters"
  >
    <DatesSelector
      :disabled="isFreeCalendarView || !sectionFiltersConfig.stayDates"
      :d-b="sdDateBegin"
      :d-e="sdDateEnd"
      :min-month="minStayDatesMonth"
      :config="stayDatesConfig"
    />
    <!-- geolocation -->
    <LpFilterSelect
      v-model="geolocations"
      :disabled="isFreeCalendarView || !sectionFiltersConfig.geolocations"
      :loading="geolocationVisitsPipe.loading"
      :options="geolocationsConfig.options as GeolocationOption[]"
      :placeholder="geolocationsConfig.placeholder"
      :searchable="geolocationsConfig.searchable"
      :search-placeholder="geolocationsConfig.searchPlaceholder"
      :loading-text="geolocationsConfig.loadingText"
      :show-select-all="!disablePremium"
      :use-virtual-scroll="!geolocationVisitsPipe.loading"
      menu-width="27rem"
      menu-max-height="27rem"
      :item-height="() => 48"
      :select-all-matching-text="t('Select all matching')"
      @update:model-value="handleTrackFilter('Geolocation')"
    >
      <template #menuheader>
        <div class="pt-2">
          <LpHorizontalMenu
            v-model="currentGeolocationOption"
          >
            <LpHorizontalMenuItem
              v-for="item in geolocationMenuItems"
              :key="item.value"
              :value="item.value"
              :unstyled="false"
            >
              {{ t(item.label) }}
            </LpHorizontalMenuItem>
          </LpHorizontalMenu>

          <LpCard
            v-if="disablePremium"
            class="p-4 mt-2 shadow-none bg-grape-100 text-grape-500"
          >
            <p>
              {{ t('Don\'t miss a single detail. Go Pro to filter by Region and City.') }}
            </p>
            <a
              class="underline !font-bold cursor-pointer text-unicorn-500"
              @click="handleUpgradeClick"
              @keydown="handleUpgradeClick"
            >
              {{ t('Upgrade now') }}
            </a>
          </LpCard>
        </div>
      </template>
      <template #default="{ option, click, active }">
        <div
          :class="[
            'px-2 rounded',
            { 'pointer-events-none': disablePremium },
          ]"
        >
          <LpMarkerBtn
            @click.prevent="click"
          >
            <LpCheckbox
              :disabled="disablePremium"
              :model-value="active"
              :input-id="String(option.value)"
            >
              <span :class="{ 'text-base-500': disablePremium }">
                {{ option.name }}
                <template v-if="option.itemCount && option.itemCount >= 0">
                  ({{ formatNumber(option.itemCount) }})
                </template>
              </span>
            </LpCheckbox>
          </LpMarkerBtn>
        </div>
      </template>

      <template #empty>
        {{ t('No results found') }}
      </template>
    </LpFilterSelect>

    <!-- device -->
    <LpFilterSelect
      v-model="devices"
      :disabled="isFreeCalendarView || !sectionFiltersConfig.devices"
      :options="devicesConfig.options"
      :placeholder="devicesConfig.placeholder"
      @update:model-value="handleTrackFilter('Device')"
    />

    <!-- source -->
    <LpFilterSelect
      v-model="source"
      :disabled="isFreeCalendarView || !sectionFiltersConfig.sources"
      :options="sourceConfig.options"
      :placeholder="sourceConfig.placeholder"
      @update:model-value="handleTrackFilter('Source')"
    />

    <!-- travel party -->
    <LpFilterSelect
      v-model="travelParty"
      :disabled="isFreeCalendarView || !sectionFiltersConfig.travelParty"
      :options="travelPartyConfig.options"
      :placeholder="travelPartyConfig.placeholder"
      @update:model-value="handleTrackFilter('Travel Party')"
    />

    <!-- hotel type -->
    <LpFilterSelect
      v-model="hotelType"
      :disabled="isFreeCalendarView || !sectionFiltersConfig.hotelType"
      :options="hotelTypeConfig.options"
      :placeholder="hotelTypeConfig.placeholder"
      x-position="right"
      @update:model-value="handleTrackFilter('Hotel Type')"
    />

    <!-- hotel category -->
    <LpFilterSelect
      v-if="isStrictlyDMOUser"
      v-model="hotelCategory"
      :disabled="isFreeCalendarView || !sectionFiltersConfig.hotelCategory"
      :options="hotelCategoryConfig.options"
      :placeholder="hotelCategoryConfig.placeholder"
      x-position="right"
      @update:model-value="handleTrackFilter('Hotel Category')"
    />

    <!-- ota -->
    <LpFilterSelect
      v-if="sectionFiltersConfig.otas"
      v-model="filteredOtas"
      :loading="otasConfig.loading"
      :disabled="isFreeCalendarView"
      :options="otasConfig.options"
      :placeholder="otasConfig.placeholder"
      :searchable="otasConfig.searchable"
      x-position="right"
      @update:model-value="handleTrackFilter('Ota')"
    />
  </div>
</template>

<style scoped>
.section-filters > * {
  flex: 0 1 auto;
  min-width: 0;
}
</style>
